import classNames from "classnames"
import { storyblokEditable } from "gatsby-source-storyblok"

import JobListings from "."
import { JobDrawerTypes } from "./JobDrawer"
import MinifiedJobListings from "./MinifiedJobListings"

import Section from "@components/elements/V2/Section"
import Typography from "@components/elements/V2/Typography"
import LayoutSection from "@components/V2/LayoutComponents/LayoutSection"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import {
  getAccentColor,
  getBackgroundColorClass,
  getGradientDirectionClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React from "react"

const StoryblokJobListings = ({
  blok,
}: Storyblok.BlokProps<Storyblok.JobListSection>) => {
  const accentColor = getAccentColor(blok.backgroundColor)

  const renderHeader = (color: Color) => (
    <>
      {blok.label && (
        <Typography
          size="body-md"
          font="grotesk"
          weight="book"
          color={color}
          text={blok.label}
        />
      )}

      {blok.title && (
        <Typography
          element="h2"
          size="h2"
          font="grotesk"
          weight="medium"
          color={color}
          text={blok.title}
        />
      )}

      {blok.description && (
        <Typography
          size="body-md"
          font="grotesk"
          weight="book"
          color={color}
          text={blok.description}
        />
      )}
    </>
  )

  if (blok.minified) {
    return (
      <LayoutItem
        as={LayoutSection}
        primaryBackgroundColor={blok.backgroundColor}
        rounded={blok.isRounded}
        className="col-span-full"
        /* @ts-expect-error */
        {...storyblokEditable(blok)}
      >
        <div className="flex w-full flex-col gap-32">
          <div className="flex w-full flex-col gap-16">
            {renderHeader(accentColor)}
          </div>

          <MinifiedJobListings blok={blok} />
        </div>
      </LayoutItem>
    )
  }

  const headerPrimaryColor = blok.headerPrimaryBackgroundColor
  const headerSecondaryColor = headerPrimaryColor
    ? blok.headerSecondaryBackgroundColor
    : undefined
  const headerAccentColor = getAccentColor(headerPrimaryColor ?? Color.White)

  const drawerData: JobDrawerTypes = {
    companyHeader: blok.companyHeader,
    companySection: blok.companySection,
    descriptionHeader: blok.descriptionHeader,
    requirementsHeader: blok.requirementsHeader,
    responsibilitiesHeader: blok.responsibilitiesHeader,
    benefitsHeader: blok.benefitsHeader,
    cultureHeader: blok.cultureHeader,
    cultureSection: blok.cultureSection,
  }

  return (
    <LayoutItem
      className="col-span-full"
      /* @ts-expect-error */
      {...storyblokEditable(blok)}
    >
      <div
        className={classNames(
          "w-full",
          headerPrimaryColor &&
            (headerSecondaryColor
              ? [
                  getGradientDirectionClass("to-b"),
                  getGradientFromColorClass(headerPrimaryColor),
                  getGradientToColorClass(headerSecondaryColor),
                ]
              : getBackgroundColorClass(headerPrimaryColor))
        )}
      >
        <Section className="flex flex-col gap-16 py-56 pt-104 sm:pb-72">
          {renderHeader(headerAccentColor)}
        </Section>
      </div>

      <Section
        className={classNames(
          "pt-32",
          getBackgroundColorClass(blok.backgroundColor)
        )}
      >
        <JobListings drawerData={drawerData} />
      </Section>
    </LayoutItem>
  )
}

export default StoryblokJobListings
